// ============================================================
// Leave Overview Page — 休假管理總覽
// 員工假別餘額表格 + 待簽核 + 即將休假 + 特休警告
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

interface StaffLeave {
  userId: string;
  userName: string;
  userAvatar: string | null;
  role: string;
  employmentType: string;
  hireDate: string;
  annualEntitled: number;
  annualUsed: number;
  annualRemaining: number;
  sickUsed: number;
  personalUsed: number;
  totalLeaveDays: number;
}

interface LeaveRecord {
  id: string;
  userId: string;
  userName: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  hours: number;
  reason?: string;
  status?: string;
}

interface OverviewData {
  year: number;
  staffOverview: StaffLeave[];
  pendingLeaves: LeaveRecord[];
  upcomingLeaves: LeaveRecord[];
  annualAlerts: { userId: string; userName: string; remaining: number; message: string }[];
  summary: {
    totalStaff: number;
    pendingCount: number;
    upcomingCount: number;
    totalAnnualEntitled: number;
    totalAnnualUsed: number;
  };
}

const LEAVE_TYPE_LABEL: Record<string, string> = {
  ANNUAL: '特休', SICK: '病假', PERSONAL: '事假', OFFICIAL: '公假',
  MENSTRUAL: '生理假', FAMILY_CARE: '家庭照顧假', MATERNITY: '產假',
  PATERNITY: '陪產假', UNPAID: '無薪假', BEREAVEMENT: '喪假',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

export function LeaveOverviewPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<OverviewData>(`/leave-overview?year=${year}`);
      setData(res);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-morandi-800">休假管理總覽</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setYear(y => y - 1)} className="w-8 h-8 rounded-full text-morandi-500 hover:bg-morandi-100 flex items-center justify-center">‹</button>
          <span className="text-lg font-semibold text-morandi-700">{year} 年</span>
          <button onClick={() => setYear(y => y + 1)} className="w-8 h-8 rounded-full text-morandi-500 hover:bg-morandi-100 flex items-center justify-center">›</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-morandi-400">載入中...</div>
      ) : data ? (
        <>
          {/* 摘要卡片 */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MiniCard label="員工數" value={data.summary.totalStaff} unit="人" />
            <MiniCard label="全體特休" value={data.summary.totalAnnualEntitled} unit="天" />
            <MiniCard label="已用特休" value={data.summary.totalAnnualUsed} unit="天" />
            <MiniCard label="待簽核" value={data.summary.pendingCount} unit="件" highlight={data.summary.pendingCount > 0} />
            <MiniCard label="即將休假" value={data.summary.upcomingCount} unit="件" />
          </div>

          {/* 特休警告 */}
          {data.annualAlerts.length > 0 && (
            <div className="space-y-2">
              {data.annualAlerts.map((a, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <span>!</span>
                  <span>{a.message}</span>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* 左欄：員工假別餘額表格 */}
            <div className="lg:col-span-2 card-morandi overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-morandi-50 text-morandi-500 text-xs">
                    <th className="text-left px-4 py-3 font-medium">員工</th>
                    <th className="text-center px-2 py-3 font-medium">到職</th>
                    <th className="text-center px-2 py-3 font-medium">特休<br/><span className="text-morandi-300">(應/用/剩)</span></th>
                    <th className="text-center px-2 py-3 font-medium">病假</th>
                    <th className="text-center px-2 py-3 font-medium">事假</th>
                    <th className="text-center px-2 py-3 font-medium">合計</th>
                  </tr>
                </thead>
                <tbody>
                  {data.staffOverview.map(staff => (
                    <tr key={staff.userId} className="border-t border-morandi-100 hover:bg-morandi-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-earth-100 flex items-center justify-center text-earth-600 text-xs font-medium shrink-0">
                            {staff.userName.slice(0, 1)}
                          </div>
                          <div>
                            <div className="font-medium text-morandi-700">{staff.userName}</div>
                            {staff.employmentType === 'PART_TIME' && (
                              <span className="text-xs text-morandi-300">PT</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="text-center px-2 py-3 text-xs text-morandi-400">{formatDate(staff.hireDate)}</td>
                      <td className="text-center px-2 py-3">
                        <div className="flex items-center justify-center gap-1 font-mono text-xs">
                          <span className="text-morandi-400">{staff.annualEntitled}</span>
                          <span className="text-morandi-200">/</span>
                          <span className="text-amber-600">{staff.annualUsed}</span>
                          <span className="text-morandi-200">/</span>
                          <span className={staff.annualRemaining <= 0 ? 'text-morandi-300' : 'text-sage-600 font-medium'}>{staff.annualRemaining}</span>
                        </div>
                        {/* 進度條 */}
                        {staff.annualEntitled > 0 && (
                          <div className="w-full h-1.5 bg-morandi-100 rounded-full mt-1">
                            <div
                              className="h-full bg-sage-400 rounded-full"
                              style={{ width: `${Math.min(100, (staff.annualUsed / staff.annualEntitled) * 100)}%` }}
                            />
                          </div>
                        )}
                      </td>
                      <td className="text-center px-2 py-3 font-mono text-xs">{staff.sickUsed || '-'}</td>
                      <td className="text-center px-2 py-3 font-mono text-xs">{staff.personalUsed || '-'}</td>
                      <td className="text-center px-2 py-3 font-mono text-xs font-medium text-morandi-700">{staff.totalLeaveDays || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 右欄 */}
            <div className="space-y-5">
              {/* 待簽核 */}
              <div className="card-morandi p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-morandi-700">待簽核假單</h2>
                  {data.pendingLeaves.length > 0 && (
                    <button
                      onClick={() => navigate('/approvals')}
                      className="text-xs text-earth-500 hover:text-earth-700"
                    >
                      前往簽核
                    </button>
                  )}
                </div>
                {data.pendingLeaves.length === 0 ? (
                  <div className="text-center py-4 text-morandi-300 text-sm">無待簽核假單</div>
                ) : (
                  <div className="space-y-2">
                    {data.pendingLeaves.map(leave => (
                      <div key={leave.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-morandi-50">
                        <div className="w-7 h-7 rounded-full bg-earth-100 flex items-center justify-center text-earth-600 text-xs shrink-0">
                          {leave.userName.slice(0, 1)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-morandi-700">{leave.userName}</div>
                          <div className="text-xs text-morandi-400">
                            {LEAVE_TYPE_LABEL[leave.leaveType]} {formatDate(leave.startDate)}–{formatDate(leave.endDate)}
                          </div>
                        </div>
                        <span className="badge bg-amber-100 text-amber-600 text-xs">{leave.hours / 8}天</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 即將休假 */}
              <div className="card-morandi p-4">
                <h2 className="font-semibold text-morandi-700 mb-3">未來兩週休假</h2>
                {data.upcomingLeaves.length === 0 ? (
                  <div className="text-center py-4 text-morandi-300 text-sm">無即將休假</div>
                ) : (
                  <div className="space-y-2">
                    {data.upcomingLeaves.map(leave => (
                      <div key={leave.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-morandi-50">
                        <div className="w-7 h-7 rounded-full bg-sage-100 flex items-center justify-center text-sage-600 text-xs shrink-0">
                          {leave.userName.slice(0, 1)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-morandi-700">{leave.userName}</div>
                          <div className="text-xs text-morandi-400">
                            {LEAVE_TYPE_LABEL[leave.leaveType]} {formatDate(leave.startDate)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-10 text-morandi-300">無資料</div>
      )}
    </div>
  );
}

function MiniCard({ label, value, unit, highlight }: {
  label: string; value: number; unit: string; highlight?: boolean;
}) {
  return (
    <div className={`card-morandi p-3 text-center ${highlight ? 'ring-1 ring-rose-300' : ''}`}>
      <div className={`text-lg font-bold font-mono ${highlight ? 'text-rose-500' : 'text-morandi-700'}`}>{value}</div>
      <div className="text-xs text-morandi-400">{label} {unit}</div>
    </div>
  );
}
